import React from "react";
import { motion } from "framer-motion";
import SuperAccordion from "Components/SuperAccordion/SuperAccordion";
import ResponsibleParty from "./ResponsibleParty";
import {
  InsuranceHeader,
  InsuranceCard,
  PlanInformation,
  GroupInformation,
  CostShare,
} from "Components/InsuranceInformationSections";
import PropTypes from "prop-types";

const InsuranceInformation = ({
  ptModel,
  validationModel,
  insuranceExpanded,
  insuranceSectionsOpen,
  insuranceCardFront,
  insuranceCardBack,
  onInsuranceExpandedChange,
  onToggleInsuranceSection,
  handleChange,
  handleCardUpload,
  handleCardRemove,
  hasInsuranceDataStarted,
  onPasteZip,
  isSelfPay,
  onSelfPayChange,
  planIndex = 0,
  coverageType = "P",
  onRemovePlan,
  showRemoveButton = false,
  onSameAsPatientAddressChange,
}) => {
  const planData = ptModel.patientPlan?.[planIndex] || {};

  const handlePayerSearchChange = (value) => {
    handleChange({
      target: {
        name: "insurancePayerName",
        dataset: { nested: `patientPlan.${planIndex}.insurancePayerName` },
        value: value,
      },
    });
  };

  const handleSelectedData = (selectedObj) => {
    if (selectedObj) {
      handleChange({
        target: {
          name: "insurancePlanID",
          dataset: { nested: `patientPlan.${planIndex}.insurancePlanID` },
          value: selectedObj.insurancePlanId || 0,
        },
      });
      handleChange({
        target: {
          name: "insurancePayerName",
          dataset: { nested: `patientPlan.${planIndex}.insurancePayerName` },
          value: selectedObj.payerName || "",
        },
      });
    } else {
      handleChange({
        target: {
          name: "insurancePlanID",
          dataset: { nested: `patientPlan.${planIndex}.insurancePlanID` },
          value: 0,
        },
      });
      handleChange({
        target: {
          name: "insurancePayerName",
          dataset: { nested: `patientPlan.${planIndex}.insurancePayerName` },
          value: "",
        },
      });
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      {planIndex === 0 && (
        <ResponsibleParty
          isSelfPay={isSelfPay}
          onSelfPayChange={onSelfPayChange}
        />
      )}

      {!isSelfPay && (
        <InsuranceHeader
          coverageType={planData?.coverage ?? coverageType}
          insuranceExpanded={insuranceExpanded}
          onInsuranceExpandedChange={onInsuranceExpandedChange}
          showRemoveButton={showRemoveButton}
          onRemovePlan={onRemovePlan}
          planIndex={planIndex}
        />
      )}

      <SuperAccordion show={insuranceExpanded && !isSelfPay}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`space-y-6 mt-4 ${
            isSelfPay ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <InsuranceCard
            planIndex={planIndex}
            insuranceSectionsOpen={insuranceSectionsOpen}
            onToggleInsuranceSection={onToggleInsuranceSection}
            insuranceCardFront={insuranceCardFront}
            insuranceCardBack={insuranceCardBack}
            handleCardUpload={handleCardUpload}
            handleCardRemove={handleCardRemove}
          />

          <PlanInformation
            planData={planData}
            planIndex={planIndex}
            coverageType={coverageType}
            ptModel={ptModel}
            insuranceSectionsOpen={insuranceSectionsOpen}
            onToggleInsuranceSection={onToggleInsuranceSection}
            handleChange={handleChange}
            handlePayerSearchChange={handlePayerSearchChange}
            handleSelectedData={handleSelectedData}
            hasInsuranceDataStarted={hasInsuranceDataStarted}
            validationModel={validationModel}
            onPasteZip={onPasteZip}
            onSameAsPatientAddressChange={onSameAsPatientAddressChange}
          />

          <GroupInformation
            planData={planData}
            planIndex={planIndex}
            insuranceSectionsOpen={insuranceSectionsOpen}
            onToggleInsuranceSection={onToggleInsuranceSection}
            handleChange={handleChange}
          />

          <CostShare
            planData={planData}
            planIndex={planIndex}
            insuranceSectionsOpen={insuranceSectionsOpen}
            onToggleInsuranceSection={onToggleInsuranceSection}
            handleChange={handleChange}
          />
        </motion.div>
      </SuperAccordion>
    </div>
  );
};

InsuranceInformation.propTypes = {
  ptModel: PropTypes.object.isRequired,
  validationModel: PropTypes.object,
  insuranceExpanded: PropTypes.bool.isRequired,
  insuranceSectionsOpen: PropTypes.object.isRequired,
  insuranceCardFront: PropTypes.object,
  insuranceCardBack: PropTypes.object,
  onInsuranceExpandedChange: PropTypes.func.isRequired,
  onToggleInsuranceSection: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleCardUpload: PropTypes.func.isRequired,
  handleCardRemove: PropTypes.func.isRequired,
  hasInsuranceDataStarted: PropTypes.func.isRequired,
  onPasteZip: PropTypes.func.isRequired,
  isSelfPay: PropTypes.bool.isRequired,
  onSelfPayChange: PropTypes.func.isRequired,
  planIndex: PropTypes.number,
  coverageType: PropTypes.string,
  onRemovePlan: PropTypes.func,
  showRemoveButton: PropTypes.bool,
  onSameAsPatientAddressChange: PropTypes.func,
};

export default InsuranceInformation;
